/**
 * Typed Models for the Staff (IT-SCE-Mitarbeiter intern) API surface.
 *
 * Diese Typen entsprechen den Response-Strukturen der Staff-Endpoints
 * in portal-api. Sie sind manuell gepflegt; Sprint 8 wird sie durch
 * `openapi-typescript`-generierte Typen ersetzen, sobald portal-api
 * vollständig annotiert ist (siehe `memory/project_portal-api-strategy.md`).
 *
 * Naming: T<Resource> (Item) | T<Resource>List (Liste) |
 * T<Resource>CreateRequest | T<Resource>UpdateRequest.
 */

// ---------------------------------------------------------------------------
// Registration Tokens (für register.scemail.de — Staff legt Token an, Kunde
// nutzt ihn beim Self-Registration). Backend: admin-api unter /api/admin/tokens
// ---------------------------------------------------------------------------

export type TTokenStatus = 'active' | 'consumed' | 'expired'

export interface TToken {
  id: number
  token: string
  description?: string | null
  max_uses?: number | null
  current_uses: number
  expires_at?: string | null
  created_by?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TTokenList {
  tokens: TToken[]
}

export interface TTokenCreateRequest {
  description?: string
  max_uses?: number
  /** RFC3339 timestamp im Format "2026-06-04T12:00:00Z" */
  expires_at?: string
}

export interface TTokenUpdateRequest {
  description?: string
  is_active?: boolean
}

/** Computed status — vom Frontend ableitbar aus is_active/max_uses/expires_at */
export function tokenStatus(t: TToken): TTokenStatus {
  if (!t.is_active) return 'consumed'
  if (t.max_uses != null && t.current_uses >= t.max_uses) return 'consumed'
  if (t.expires_at && new Date(t.expires_at) < new Date()) return 'expired'
  return 'active'
}

// ---------------------------------------------------------------------------
// Service-Catalog (Staff-Verwaltung der Service-Definitionen)
// Backend: admin-api /api/admin/catalog (camelCase JSON, nicht snake_case)
// ---------------------------------------------------------------------------

export interface TServiceCatalogEntry {
  id: number
  erpnextItemCode: string
  name: string
  slug?: string | null
  description?: string | null
  longDescription?: string | null
  category: string
  basePrice?: number | null
  currency: string
  billingCycle: string
  isVisible: boolean
  isActive: boolean
  isFeatured: boolean
  displayOrder: number
  icon?: string | null
  lastSyncAt?: string | null
  syncStatus: string
  createdAt: string
  updatedAt: string
}

export interface TServiceCatalogList {
  services: TServiceCatalogEntry[]
}

/** Felder die per PATCH /catalog/services/{id} setzbar sind. Backend nimmt camelCase. */
export interface TServiceCatalogUpdateRequest {
  isVisible?: boolean
  isActive?: boolean
  isFeatured?: boolean
  displayOrder?: number
  description?: string
  longDescription?: string
  icon?: string
  /** Optional: Änderungsgrund — wird in Audit-Log geschrieben */
  changeReason?: string
}

export interface TServiceCatalogSyncResult {
  totalServices: number
  synced: number
  errors: number
  timestamp: string
}

/** Audit-Eintrag — Backend-Schema noch nicht final dokumentiert; provisorisch. */
export interface TServiceAuditEntry {
  id: number
  serviceId: number
  field: string
  oldValue?: string | null
  newValue?: string | null
  changedBy: string
  changeReason?: string
  changedAt: string
}

// ---------------------------------------------------------------------------
// Subscription Plans (Staff-Verwaltung der Plan-Sichtbarkeit)
// Backend: admin-api -> erpnext-sync /admin/sync/subscription-plans
// Schema: PortalSubscriptionPlan in erpnext-sync (snake_case)
// ---------------------------------------------------------------------------

export interface TSubscriptionPlan {
  id: number
  erpnext_plan_id: string
  name: string
  description?: string | null
  billing_interval: string  // 'Month' | 'Year' | 'Quarter' (ERPNext-Format)
  rate: number
  currency: string
  plan_type?: string | null
  is_active: boolean
  // Item-Reference (für Catalog-Gruppierung)
  item_code?: string | null
  item_name?: string | null
  item_description?: string | null
  display_order: number
  variant_name?: string | null
  variant_description?: string | null
  is_catalog_visible: boolean
  // Timestamps
  last_sync_at?: string | null
  sync_hash?: string | null
  created_at: string
  updated_at: string
}

export interface TSubscriptionPlanList {
  plans: TSubscriptionPlan[]
  total?: number
}

export interface TSubscriptionPlanVisibilityRequest {
  is_catalog_visible: boolean
}

export interface TSubscriptionPlanVariantRequest {
  variant_name?: string
  variant_description?: string
  display_order?: number
}

export interface TSubscriptionPlanItemRequest {
  item_code?: string
}

export interface TSubscriptionPlansStats {
  total: number
  visible: number
  hidden: number
  active: number
  by_billing_interval?: Record<string, number>
}

// ---------------------------------------------------------------------------
// Cortex Tenants & Grants (Staff-Onboarding für Cortex-Self-Service)
// ---------------------------------------------------------------------------

export interface TCortexTenant {
  id: string
  display_name: string
  keycloak_group: string
  identity?: string
  role: 'mcp-portal' | 'mcp-workspace' | 'mcp-admin'
  created_at: string
  member_count?: number
  grant_count?: number
}

export interface TCortexTenantList {
  tenants: TCortexTenant[]
  total: number
}

export interface TCortexTenantCreateRequest {
  display_name: string
  keycloak_group: string
  identity: string
  role: 'mcp-portal' | 'mcp-workspace' | 'mcp-admin'
}

export interface TCortexGrant {
  id: number
  tenant_id: string
  service: string
  scope?: string
  granted_by: string
  granted_at: string
  revoked_at?: string | null
}

export interface TCortexGrantList {
  grants: TCortexGrant[]
  total: number
}

export interface TCortexGrantCreateRequest {
  tenant_id: string
  service: string
  scope?: string
}

// ---------------------------------------------------------------------------
// VM-Assignment (Staff-Zuweisung von VMs zu Customer/Organization)
// ---------------------------------------------------------------------------

export interface TVMAssignment {
  vm_id: number
  vm_name: string
  vm_status?: string
  customer_id?: number | null
  customer_email?: string | null
  organization_id?: number | null
  organization_slug?: string | null
  organization_name?: string | null
  monthly_rate?: number | null
  currency?: string
  notes?: string
  assigned_at?: string
  assigned_by?: string
}

export interface TVMAssignmentList {
  vms: TVMAssignment[]
  total: number
  unassigned_count: number
}

export interface TVMAssignmentRequest {
  customer_id?: number | null
  organization_id?: number | null
  monthly_rate?: number | null
  currency?: string
  notes?: string
}

// ---------------------------------------------------------------------------
// Sync (ERPNext-Sync Status + Logs)
// ---------------------------------------------------------------------------

export interface TSyncRun {
  id: number
  service: 'subscriptions' | 'subscription-plans' | 'customers' | 'orgs'
  status: 'success' | 'partial' | 'failed' | 'running'
  started_at: string
  completed_at?: string | null
  duration_ms?: number
  records_total: number
  records_created: number
  records_updated: number
  records_failed: number
  error?: string
}

export interface TSyncRunList {
  runs: TSyncRun[]
  total: number
}

export interface TSyncStatus {
  last_run?: TSyncRun | null
  recent: TSyncRun[]
  schedule: Record<string, string> // service → cron schedule
}
